import Swal from "sweetalert2";

import { formatPhone, isEmail, isPhone } from "../../../lib/validation";
import type { ContactRow } from "../../../types";

import { escapeHtml } from "./escape";

export type ContactModalResult = Pick<
  ContactRow,
  | "new_firstname"
  | "new_lastname"
  | "new_title"
  | "new_email"
  | "new_phone"
  | "new_cell_phone"
  | "new_fax"
  | "new_extension"
  | "new_region"
> & {
  // Tri-state semantics: true = user explicitly set primary, false = user
  // unset it (was primary), undefined = checkbox unchanged from initial.
  // Step4's handler interprets undefined as "leave the flag as-is".
  is_primary?: boolean;
};

interface OpenArgs {
  mode: "create" | "edit";
  initial?: Partial<ContactRow>;
}

export async function openContactModal({
  mode,
  initial,
}: OpenArgs): Promise<ContactModalResult | null> {
  const isCreate = mode === "create";
  const result = await Swal.fire<ContactModalResult>({
    title: isCreate ? "Add Contact" : "Edit Contact",
    width: 640,
    showCancelButton: true,
    confirmButtonText: isCreate ? "Add" : "Save",
    focusConfirm: false,
    html: `
      <div class="text-start">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label" for="modal-c-firstname">
              First Name <span class="text-danger" aria-hidden="true">*</span>
            </label>
            <input id="modal-c-firstname" type="text" class="form-control"
              value="${escapeHtml(initial?.new_firstname)}" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="modal-c-lastname">
              Last Name <span class="text-danger" aria-hidden="true">*</span>
            </label>
            <input id="modal-c-lastname" type="text" class="form-control"
              value="${escapeHtml(initial?.new_lastname)}" />
          </div>
          <div class="col-md-12">
            <label class="form-label" for="modal-c-title">
              Job Title <span class="text-danger" aria-hidden="true">*</span>
            </label>
            <input id="modal-c-title" type="text" class="form-control"
              value="${escapeHtml(initial?.new_title)}" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="modal-c-email">
              Email <span class="text-danger" aria-hidden="true">*</span>
            </label>
            <input id="modal-c-email" type="email" class="form-control"
              value="${escapeHtml(initial?.new_email)}" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="modal-c-phone">
              Phone <span class="text-danger" aria-hidden="true">*</span>
            </label>
            <input id="modal-c-phone" type="tel" class="form-control"
              placeholder="(000) 000-0000"
              value="${escapeHtml(formatPhone(initial?.new_phone))}" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="modal-c-cellphone">Cell Phone</label>
            <input id="modal-c-cellphone" type="tel" class="form-control"
              placeholder="(000) 000-0000"
              value="${escapeHtml(formatPhone(initial?.new_cell_phone))}" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="modal-c-fax">Fax</label>
            <input id="modal-c-fax" type="tel" class="form-control"
              placeholder="(000) 000-0000"
              value="${escapeHtml(formatPhone(initial?.new_fax))}" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="modal-c-ext">Extension</label>
            <input id="modal-c-ext" type="text" class="form-control"
              value="${escapeHtml(initial?.new_extension)}" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="modal-c-region">Region</label>
            <input id="modal-c-region" type="text" class="form-control"
              value="${escapeHtml(initial?.new_region)}" />
          </div>
          <div class="col-md-12">
            <div class="form-check">
              <input id="modal-c-primary" type="checkbox" class="form-check-input"
                ${initial?.is_primary ? "checked" : ""} />
              <label class="form-check-label" for="modal-c-primary">
                <i class="fa-solid fa-star text-warning me-1" aria-hidden="true"></i>
                Set as the primary contact for this supplier
              </label>
              <small class="d-block text-muted">Exactly one contact is the primary. Setting this here will unset whichever contact currently has the flag.</small>
            </div>
          </div>
        </div>
      </div>
    `,
    didOpen: () => {
      // Live phone-mask formatting on type for the three tel fields.
      ["modal-c-phone", "modal-c-cellphone", "modal-c-fax"].forEach((id) => {
        const el = document.getElementById(id) as HTMLInputElement | null;
        if (!el) return;
        el.addEventListener("input", () => {
          el.value = formatPhone(el.value);
        });
      });
    },
    preConfirm: (): ContactModalResult | false => {
      const firstname = (document.getElementById("modal-c-firstname") as HTMLInputElement).value.trim();
      const lastname = (document.getElementById("modal-c-lastname") as HTMLInputElement).value.trim();
      const title = (document.getElementById("modal-c-title") as HTMLInputElement).value.trim();
      const email = (document.getElementById("modal-c-email") as HTMLInputElement).value.trim();
      const phone = (document.getElementById("modal-c-phone") as HTMLInputElement).value.trim();
      const cellphone = (document.getElementById("modal-c-cellphone") as HTMLInputElement).value.trim();
      const fax = (document.getElementById("modal-c-fax") as HTMLInputElement).value.trim();
      const ext = (document.getElementById("modal-c-ext") as HTMLInputElement).value.trim();
      const region = (document.getElementById("modal-c-region") as HTMLInputElement).value.trim();
      const primaryChecked = (document.getElementById("modal-c-primary") as HTMLInputElement).checked;

      if (!firstname) { Swal.showValidationMessage("First Name is required"); return false; }
      if (!lastname) { Swal.showValidationMessage("Last Name is required"); return false; }
      if (!title) { Swal.showValidationMessage("Job Title is required"); return false; }
      if (!email) { Swal.showValidationMessage("Email is required"); return false; }
      if (!isEmail(email)) { Swal.showValidationMessage("Enter a valid email address"); return false; }
      if (!phone) { Swal.showValidationMessage("Phone is required"); return false; }
      if (!isPhone(phone)) { Swal.showValidationMessage("Phone must match (000) 000-0000"); return false; }
      if (cellphone && !isPhone(cellphone)) { Swal.showValidationMessage("Cell phone must match (000) 000-0000"); return false; }
      if (fax && !isPhone(fax)) { Swal.showValidationMessage("Fax must match (000) 000-0000"); return false; }

      // Tri-state: only emit is_primary when it differs from the initial
      // value, otherwise leave the field undefined so Step4's handler
      // leaves the row's flag alone.
      const initialPrimary = !!initial?.is_primary;
      const primaryChanged = primaryChecked !== initialPrimary;
      return {
        new_firstname: firstname,
        new_lastname: lastname,
        new_title: title,
        new_email: email,
        new_phone: phone,
        new_cell_phone: cellphone || undefined,
        new_fax: fax || undefined,
        new_extension: ext || undefined,
        new_region: region || undefined,
        ...(primaryChanged ? { is_primary: primaryChecked } : {}),
      };
    },
  });
  return result.isConfirmed && result.value ? result.value : null;
}
